<template>
  <div class="flex-grow h-full" id="viewDiv">
    <LPISLayerComponent
      :lpisUrl="lpisUrl"
      :view="mapState.view"
      :showLpis="showLpis"
      :map="mapState.map"
      :LpisHerdNo="LpisHerdNo"
      :source="lpisSource"
      :dafm="dafmLpis"
      :linkedIds="lpisLinkedIds"
      :isMapBusy="isMapBusy"
      :showContext="lpisContextVisible"
      :showLinked="lpisLinkedVisible"
      @filteredFeaturesReady="setFeaturesLocations"
      @linkedFeaturesReady="setLpisLinkedFeatures"
      @layerLoaded="setLPISLayer"
    />

    <!-- Custom Layer Toggle Widget, shown in an ArcGIS Expand widget (a
         layers button at the top left that opens it as a popup) -->
    <div ref="layerToggleEl">
    <CustomLayerToggle
      v-if="showLayerToggle && layerRefs"
      :layers="layerRefs"
      :soilAnalysisContext="soilAnalysisContext"
      :sampleZones="sampleZones"
      :lpisAvailable="!!showLpis && lpisSource === 'dafm' && !!dafmLpis?.available"
      :lpisContextVisible="lpisContextVisible"
      :lpisLinkedVisible="lpisLinkedVisible"
      @layer-visibility-changed="onLayerVisibilityChanged"
      @layer-opacity-changed="onLayerOpacityChanged"
      @global-label-scale-changed="onGlobalLabelScaleChanged"
    />
    </div>
    
    <!-- Soil legend above the soil property selector, in the map's bottom-left
         corner beside any LPIS panels -->
    <div ref="soilWidgetsEl" v-show="hasSoilAnalysisResults" class="soil-widgets">
      <SoilAnalysisLegend
        v-if="hasSoilAnalysisResults"
        :legendData="soilAnalysisLegendData"
        :disabled="!soilAnalysisEnabled"
      />
      <SoilPropertySelector
        v-if="hasSoilAnalysisResults"
        :soilAnalysisContext="soilAnalysisContext"
        :disabled="!soilAnalysisEnabled"
        @soil-data-updated="onSoilDataUpdated"
      />
    </div>
    
    <!-- Coordinate Popup Widget -->
    <div 
      v-if="coordinatePopup.show" 
      class="coordinate-popup-widget"
      :style="{ transform: 'translateX(-50%)', left: '50%', bottom: '20px' }"
    >
      <div class="coordinate-popup-content">
        <div class="coordinate-popup-header">
          <svg class="coordinate-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
          </svg>
          <span class="coordinate-title">Coordinates</span>
          <button @click.stop="closeCoordinatePopup" class="coordinate-close-btn">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6m0 0l6-6m-6 6l-6 6"></path>
            </svg>
          </button>
        </div>
        <div class="coordinate-values" @click="copyCoordinates" title="Click to copy coordinates">
          <div class="coordinate-row">
            <span class="coordinate-label">Lat:</span>
            <span class="coordinate-value">{{ coordinatePopup.latitude }}</span>
          </div>
          <div class="coordinate-row">
            <span class="coordinate-label">Lng:</span>
            <span class="coordinate-value">{{ coordinatePopup.longitude }}</span>
          </div>
        </div>
        <div class="coordinate-copy-hint">Click coordinates to copy</div>
      </div>
    </div>

    <!-- Snapping Toggle -->
    <div
      v-show="snappingControl.show"
      ref="snappingToggleEl"
      class="esri-widget p-2"
      title="Snap to nearby parcel and LPIS boundaries while drawing. Hold Ctrl while adding a point to temporarily reverse this setting."
    >
      <div class="flex">
        <p class="inline-flex items-center mr-2">Snapping</p>
        <label class="inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            class="sr-only peer"
            :checked="snappingControl.enabled"
            @change="toggleSnapping($event.target.checked)"
          />
          <div
            class="relative w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-green"
          ></div>
        </label>
      </div>
      <!-- ArcGIS treats Ctrl as a temporary inversion of the current
           snapping state, so the hint changes with the toggle. -->
      <p class="mt-1 text-xs text-gray-500">
        Hold <kbd class="px-1 border rounded bg-gray-100 text-gray-700">Ctrl</kbd>
        to temporarily {{ snappingControl.enabled ? "disable" : "enable" }}
      </p>
    </div>
  </div>
</template>

<script>
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import Multipoint from "@arcgis/core/geometry/Multipoint";
import Polyline from "@arcgis/core/geometry/Polyline";
import Extent from "@arcgis/core/geometry/Extent";
import Color from "@arcgis/core/Color";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import TextSymbol from "@arcgis/core/symbols/TextSymbol";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils.js";
import Locate from "@arcgis/core/widgets/Locate";
import Search from "@arcgis/core/widgets/Search";
import Expand from "@arcgis/core/widgets/Expand";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
// import FarmManager from "@/ArcGis/ArcgisFeature";
import ArcgisFeature from "@/Partials/Map/Util/ArcgisFeature";
import {
  ref,
  reactive,
  watch,
  toRef,
  onMounted,
  onBeforeUnmount,
  getCurrentInstance,
  shallowReactive,
  nextTick,
  computed
} from "vue";
import SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer.js";
import PictureMarkerSymbol from "@arcgis/core/symbols/PictureMarkerSymbol.js";
import LPISLayerComponent from "./Map/LPISLayerComponent.vue";
import CustomLayerToggle from "./Map/components/CustomLayerToggle.vue";
import SoilPropertySelector from "./Map/components/SoilPropertySelector.vue";
import SoilAnalysisLegend from "./Map/components/SoilAnalysisLegend.vue";
import { useMapView } from "./Map/composables/BaseView";
import { scaleForZoom } from "./Map/composables/ZoomLevels";
import {
  createDropoffPointsLayer,
  createParcelGraphic,
  createParcelEditingGraphic,
  createPathsGraphic,
  createSampleGraphic,
  createSamplerLocationLayer,
  createGeoreferenceMarkerGraphics,
} from "./Map/composables/LayerBuilder";
import { useSoilAnalysisLayer } from "./Map/composables/SoilAnalysisLayer";

// Scale used when zooming to a sample path, GPS sample or georeference, so the
// map does not zoom in too far on small targets.
const PATH_FOCUS_SCALE = 5000;

export default {
  props: {
    farmer: Object,
    showFarmManager: Boolean,
    paths: [Array, Object],
    fields: [Array, Object],
    apikey: String,
    center: Array,
    zoom: [Number, String],
    sampleZones: [Array, Object],
    LpisHerdNo: String,
    showLpis: [Boolean, String],
    // "isec" or "dafm"
    lpisSource: String,
    // DAFM LPIS layer config, only present for signed-in users
    dafmLpis: Object,
    // DAFM LPIS parcel ids linked to the farmer on the map
    lpisLinkedIds: Array,
    dropoff_points: [Array, Boolean],
    sampler_location: [Object, Boolean],
    lpisUrl: String,
    showLayerToggle: {
      type: Boolean,
      default: false
    },
    soilAnalysisContext: {
      type: Object,
      default: null
      // Expected structure: { task: String|Number, farm: String|Number, soilTypes: Array }
    }
  },
  components: {
    LPISLayerComponent,
    CustomLayerToggle,
    SoilPropertySelector,
    SoilAnalysisLegend,
  },
  setup(props, context) {
    //getting current instance
    const internalInstance = getCurrentInstance();
    const $emitter =
      internalInstance.appContext.config.globalProperties.$emitter;

    //get reactive variables
    const farmer = toRef(props, "farmer");
    const paths = toRef(props, "paths");
    const LpisHerdNo = toRef(props, "LpisHerdNo");
    const showLpis = toRef(props, "showLpis");
    const fields = toRef(props, "fields");
    const sampleZones = toRef(props, "sampleZones");
    const showFarmManager = toRef(props, "showFarmManager");
    const apikey = props.apikey;
    const lpisUrl = props.lpisUrl;
    const center = [props.center[0], props.center[1]];
    const zoom = typeof props.zoom === 'string' ? parseInt(props.zoom) : props.zoom;
    // Handle different data types for these props
    const dropoff_points = Array.isArray(props.dropoff_points) ? toRef(props, "dropoff_points") : ref([]);
    const sampler_location = typeof props.sampler_location === 'object' && props.sampler_location !== null ? toRef(props, "sampler_location") : ref(null);
    const showLayerToggle = toRef(props, "showLayerToggle");
    // DAFM LPIS parcels in grey, toggled in the layer widget; off by default
    const lpisContextVisible = ref(false);
    // The farmer's linked DAFM LPIS parcels in pink; on by default
    const lpisLinkedVisible = ref(true);

    // On-map snapping toggle. "show" is true only while a parcel is being
    // drawn or reshaped; "enabled" is remembered for the life of the map.
    const snappingControl = reactive({
      show: false,
      enabled: false,
    });
    const snappingToggleEl = ref(null);
    const layerToggleEl = ref(null);
    const soilWidgetsEl = ref(null);
    // The search, zoom and locate controls sit at the top right on phones and
    // tablets, and at the bottom right on wider screens, with the search at
    // the bottom. The layers button stays at the top left. 767px matches
    // App.vue's MOBILE_BREAKPOINT and Tailwind's md: breakpoint.
    const narrowScreen = window.matchMedia("(max-width: 767px)");
    let searchWidget = null;
    let searchExpand = null;
    let locateWidget = null;
    let layersExpand = null;

    function placeMapControls() {
      const ui = mapState.view?.ui;
      if (!ui || !searchWidget) return;
      const narrow = narrowScreen.matches;
      // Narrow screens open the search from a button, so the open layers panel
      // at the top left doesn't run into a search box
      ui.remove([searchWidget, searchExpand]);
      searchExpand.collapse();
      searchExpand.content = narrow ? searchWidget.container : null;
      const search = narrow ? searchExpand : searchWidget;
      ui.add(search);
      if (narrow) {
        ui.move([search, "zoom", locateWidget], "top-right");
      } else {
        ui.move(["zoom", locateWidget, search], "bottom-right");
      }
    }

    // Reactive state for coordinate popup
    const coordinatePopup = reactive({
      show: false,
      latitude: '',
      longitude: ''
    });
    
    // Reactive state for soil analysis legend
    const soilAnalysisLegendData = ref(null);
    
    // Reactive state for soil analysis layer visibility
    const soilAnalysisEnabled = ref(true);
    
    // Computed property to check if there are actual soil analysis results
    const hasSoilAnalysisResults = computed(() => {
      return !!(
        sampleZones.value && 
        Array.isArray(sampleZones.value) &&
        sampleZones.value.length > 0
      )
    })

    //The variable to hold the map and view created by BaseView composable
    const mapState = shallowReactive({
      view: null,
      map: null,
    });

    //creating Graphics layer
    const smapSamplePathsLayer = new GraphicsLayer();
    const farmerLayer = new GraphicsLayer();
    const farmSampleZoneLayer = new GraphicsLayer();
    const farmIdentifyLayer = new GraphicsLayer();
    const farmFieldsLayer = new GraphicsLayer();
    const parcelLabelLayer = new GraphicsLayer();
    const samplePathsLabelLayer = new GraphicsLayer();
    const sampleZoneLabelLayer = new GraphicsLayer();
    
    // Set initial layer visibility to match CustomLayerToggle defaults
    farmerLayer.visible = true; // Farms Locations should be on by default
    
    // Parcels should be hidden by default
    farmFieldsLayer.visible = false;
    parcelLabelLayer.visible = false;
    
    // Store current global label scale (initialize to match original values)
    let globalLabelScale = 15000;
    
    // Set scale-based visibility for label layers
    // Scale values: larger numbers = more zoomed out, smaller numbers = more zoomed in
    // Labels should only show when zoomed in (at smaller scale values)
    const parcelLabelMinScale = 15000;
    const samplePathsLabelMinScale = 15000;
    const sampleZoneLabelMinScale = 15000;
    const manualGeoreferencesLabelMinScale = 15000;
    let taskLabelMinScale = 150000; // Made mutable for layer toggle control
    let farmerLabelMinScale = 150000; // Separate variable for farmer labels
    
    parcelLabelLayer.maxScale = 0;      // Always visible when zoomed in enough
    parcelLabelLayer.minScale = globalLabelScale;  // Use global scale instead of hardcoded
    
    samplePathsLabelLayer.maxScale = 0;
    samplePathsLabelLayer.minScale = globalLabelScale;
    
    sampleZoneLabelLayer.maxScale = 0;
    sampleZoneLabelLayer.minScale = globalLabelScale;
    
    // Keep smapSamplePathsLayer visible at all scales (for path lines only)
    
    const sampleGeoreferencesLayer = new GraphicsLayer();
    const manualGeoreferencesLabelLayer = new GraphicsLayer();
    
    // Set scale-based visibility for manual georeference labels only
    manualGeoreferencesLabelLayer.maxScale = 0;
    manualGeoreferencesLabelLayer.minScale = globalLabelScale;
    
    // Keep sampleGeoreferencesLayer (points/lines) visible at all scales
    
    const sketchLayer = new GraphicsLayer();
    const coordinateMarkerLayer = new GraphicsLayer();
    const taskLocationsLayer = new GraphicsLayer();
    const taskLabelLayer = new GraphicsLayer();
    
    // Create FeatureLayer for farmer locations with clustering
    const farmerLocationsLayer = new FeatureLayer({
      title: "Farmer Locations", // Add title for debugging
      source: [], // Will be populated dynamically
      fields: [
        {
          name: "ObjectID",
          alias: "ObjectID",
          type: "oid"
        },
        {
          name: "farmer_id",
          alias: "Farmer ID",
          type: "string"
        },
        {
          name: "farmer_name",
          alias: "Farmer Name",  
          type: "string"
        },
        {
          name: "farm_name",
          alias: "Farm Name",
          type: "string"
        },
        {
          name: "advisor",
          alias: "Advisor",
          type: "string"
        },
        {
          name: "address",
          alias: "Address",
          type: "string"
        }
      ],
      objectIdField: "ObjectID",
      geometryType: "point", 
      spatialReference: { wkid: 3857 }, // Match the geometry coordinates
      visible: true,  // Ensure layer is visible
      minScale: 0,    // Always visible regardless of scale
      maxScale: 0,    // Always visible regardless of scale
      renderer: {
        type: "simple",
        symbol: {
          type: "picture-marker",
          url: "/img/widget/icon-pin@40x40.svg",
          width: 30,
          height: 30
        }
      },
      popupTemplate: {
        title: "{farmer_name}",
        content: `
          <div>
            <strong>Farmer:</strong> {farmer_name}<br>
            <strong>Farm:</strong> {farm_name}<br>
            <strong>Advisor:</strong> {advisor}<br>
            <strong>Address:</strong> {address}
          </div>
        `
      },
      // Enable clustering for performance with many pins
      // Temporarily disable clustering for debugging
      featureReduction: {
        type: "cluster",
        clusterRadius: 40,
        clusterMinSize: 28,
        clusterMaxSize: 40,
        symbol: {
          type: "picture-marker",
          url: "/img/widget/icon-pin@40x40.svg",
          width: 24,
          height: 24
        },
        labelingInfo: [{
          labelExpressionInfo: {
            expression: "$feature.cluster_count"
          },
          symbol: {
            type: "text",
            color: "white",
            haloColor: "black",
            haloSize: 1,
            font: {
              size: 13,
              family: "Arial",
              weight: "bold"
            },
            yoffset: 4
          },
          labelPlacement: "center-center"
        }],
        popupTemplate: {
          title: "Farmers in this area",
          content: "This cluster represents {cluster_count} farmers."
        }
      }
    });
    
    const farmerLabelLayer = new GraphicsLayer();
    
    // Task and farmer labels always visible (no scale restrictions for now)

    // Create reference object for layer toggle component (non-reactive to avoid ArcGIS proxy issues)
    const layerRefs = {
      farmFieldsLayer,
      farmSampleZoneLayer,
      parcelLabelLayer,
      sampleZoneLabelLayer,
      farmerLayer,
      smapSamplePathsLayer,
      sampleGeoreferencesLayer,
      samplePathsLabelLayer,
      manualGeoreferencesLabelLayer,
      coordinateMarkerLayer,
      taskLocationsLayer,
      taskLabelLayer,
      farmerLocationsLayer,
      farmerLabelLayer
    };

    // Track if samples layer is active (present in map) using a Vue ref
    const samplesLayerActive = ref(true);
    
    // Track which label layers are enabled via toggles (separate from scale-based visibility)
    const labelToggleStates = ref({
      parcelLabels: true,
      samplePathLabels: true, 
      sampleZoneLabels: true,
      manualGeoreferencesLabels: true
    });
    
    // Manual watcher for paths, so we can stop/restart it
    let stopPathsWatcher = null;
    // Layer toggle event handlers
    function onLayerVisibilityChanged({ layerId, visible }) {
      if (layerId === 'lpis') {
        lpisContextVisible.value = visible;
      }
      if (layerId === 'lpisLinked') {
        lpisLinkedVisible.value = visible;
      }

      // Handle soil analysis layer toggle
      if (layerId === 'soilAnalysis') {
        soilAnalysisEnabled.value = visible;
        // Control actual layer visibility
        if (layerRefs.soilAnalysisLayer) {
          layerRefs.soilAnalysisLayer.visible = visible;
        }
        if (layerRefs.soilAnalysisLabelLayer) {
          layerRefs.soilAnalysisLabelLayer.visible = visible;
        }
        // Clear soil analysis displays when turned off
        if (!visible) {
          clearSoilAnalysisDisplay();
          soilAnalysisLegendData.value = null;
        }
      }

      // Explicitly handle samples toggle to ensure all relevant layers are removed/added from the map
      if (layerId === 'samples') {
        samplesLayerActive.value = visible;
        if (stopPathsWatcher) {
          stopPathsWatcher();
          stopPathsWatcher = null;
        }
        const map = mapState.map;
        if (!map) return;
        // Remove all sample layers first
        [layerRefs.smapSamplePathsLayer, layerRefs.sampleGeoreferencesLayer, layerRefs.samplePathsLabelLayer, layerRefs.manualGeoreferencesLabelLayer].forEach(layer => {
          if (layer && map.layers.includes(layer)) map.remove(layer);
        });
        if (visible) {
          // Add sample layers back in correct order, maintaining proper layer stack position
          // Insert them at the right position relative to other layers
          const soilAnalysisLayerIndex = map.layers.indexOf(layerRefs.soilAnalysisLayer);
          const insertIndex = soilAnalysisLayerIndex > -1 ? soilAnalysisLayerIndex + 2 : map.layers.length;
          
          map.add(layerRefs.smapSamplePathsLayer, insertIndex);
          map.add(layerRefs.sampleGeoreferencesLayer, insertIndex + 1);
          map.add(layerRefs.samplePathsLabelLayer, insertIndex + 2);
          map.add(layerRefs.manualGeoreferencesLabelLayer, insertIndex + 3);
          
          // Restart watcher to redraw paths
          stopPathsWatcher = watch(paths, handlePathsUpdate, { immediate: true });
          // Restore label layer visibility and toggle state
          if (layerRefs.samplePathsLabelLayer) {
            labelToggleStates.value.samplePathLabels = true;
            layerRefs.samplePathsLabelLayer.visible = true;
            layerRefs.samplePathsLabelLayer.minScale = globalLabelScale;
          }
          if (layerRefs.manualGeoreferencesLabelLayer) {
            labelToggleStates.value.manualGeoreferencesLabels = true;
            layerRefs.manualGeoreferencesLabelLayer.minScale = globalLabelScale;
          }
        } else {
          // Hide label layer and update toggle state
          if (layerRefs.samplePathsLabelLayer) {
            labelToggleStates.value.samplePathLabels = false;
            layerRefs.samplePathsLabelLayer.visible = false;
          }
          if (layerRefs.manualGeoreferencesLabelLayer) {
            labelToggleStates.value.manualGeoreferencesLabels = false;
            layerRefs.manualGeoreferencesLabelLayer.visible = false;
          }
        }
        return;
      }

      // Handle parcel layer toggle
      if (layerId === 'parcels') {
        if (layerRefs.farmFieldsLayer) {
          layerRefs.farmFieldsLayer.visible = visible;
        }
        // Update parcel labels toggle state when parent layer changes
        if (!visible) {
          // When parcels are disabled, disable parcel labels too
          labelToggleStates.value.parcelLabels = false;
          if (layerRefs.parcelLabelLayer) {
            layerRefs.parcelLabelLayer.visible = false;
          }
        } else {
          // When parcels are re-enabled, restore parcel labels based on scale
          labelToggleStates.value.parcelLabels = true;
          if (layerRefs.parcelLabelLayer) {
            layerRefs.parcelLabelLayer.visible = true;
            layerRefs.parcelLabelLayer.minScale = globalLabelScale;
          }
        }
      }

      // Handle farmer locations layer toggle
      if (layerId === 'farmerLayer') {
        if (layerRefs.farmerLayer) {
          layerRefs.farmerLayer.visible = visible;
        }
      }

      // Handle individual label layer toggles  
      if (layerId === 'parcelLabels') {
        labelToggleStates.value.parcelLabels = visible;
        if (layerRefs.parcelLabelLayer) {
          if (visible) {
            layerRefs.parcelLabelLayer.visible = true;
            layerRefs.parcelLabelLayer.minScale = globalLabelScale;
          } else {
            layerRefs.parcelLabelLayer.visible = false;
          }
        }
      }

      if (layerId === 'samplePathLabels') {
        labelToggleStates.value.samplePathLabels = visible;
        // Handle both sample path labels and manual georeference labels
        if (layerRefs.samplePathsLabelLayer) {
          if (visible) {
            layerRefs.samplePathsLabelLayer.visible = true;
            layerRefs.samplePathsLabelLayer.minScale = globalLabelScale;
          } else {
            layerRefs.samplePathsLabelLayer.visible = false;
          }
        }
        if (layerRefs.manualGeoreferencesLabelLayer) {
          if (visible) {
            layerRefs.manualGeoreferencesLabelLayer.minScale = globalLabelScale;
          } else {
            layerRefs.manualGeoreferencesLabelLayer.visible = false;
          }
        }
      }

      if (layerId === 'sampleZoneLabels') {
        labelToggleStates.value.sampleZoneLabels = visible;
        if (layerRefs.sampleZoneLabelLayer) {
          if (visible) {
            layerRefs.sampleZoneLabelLayer.visible = true;
            layerRefs.sampleZoneLabelLayer.minScale = globalLabelScale;
          } else {
            layerRefs.sampleZoneLabelLayer.visible = false;
          }
        }
      }

      // You can add custom logic here, such as:
      // - Saving user preferences
      // - Triggering related layer changes
      // - Emitting events to parent components
      context.emit('layer-visibility-changed', { layerId, visible });
    }

    function onLayerOpacityChanged({ layerId, opacity }) {
      
      context.emit('layer-opacity-changed', { layerId, opacity });
    }

    function onGlobalLabelScaleChanged({ scale }) {
      // Update the global scale variable
      globalLabelScale = scale;
      
      // Update the actual minScale property on label layers based on toggle states
      // Use hybrid approach: visible=false when toggled off, minScale when toggled on
      
      if (layerRefs.parcelLabelLayer) {
        if (labelToggleStates.value.parcelLabels) {
          layerRefs.parcelLabelLayer.visible = true;
          layerRefs.parcelLabelLayer.minScale = scale;
        } else {
          layerRefs.parcelLabelLayer.visible = false;
        }
      }
      
      if (layerRefs.samplePathsLabelLayer) {
        if (labelToggleStates.value.samplePathLabels) {
          layerRefs.samplePathsLabelLayer.visible = true;
          layerRefs.samplePathsLabelLayer.minScale = scale;
        } else {
          layerRefs.samplePathsLabelLayer.visible = false;
        }
      }
      
      if (layerRefs.manualGeoreferencesLabelLayer) {
        if (labelToggleStates.value.samplePathLabels) { // Use samplePathLabels toggle state
          layerRefs.manualGeoreferencesLabelLayer.minScale = scale;
        } else {
          layerRefs.manualGeoreferencesLabelLayer.visible = false;
        }
      }
      
      if (layerRefs.sampleZoneLabelLayer) {
        if (labelToggleStates.value.sampleZoneLabels) {
          layerRefs.sampleZoneLabelLayer.visible = true;
          layerRefs.sampleZoneLabelLayer.minScale = scale;
        } else {
          layerRefs.sampleZoneLabelLayer.visible = false;
        }
      }
      
      // Update task and farmer labels (these don't have toggles yet, so always use scale)
      if (taskLabelLayer) {
        taskLabelLayer.minScale = scale;
        taskLabelMinScale = scale; // Update the variable for consistency
      }
      if (farmerLabelLayer) {
        farmerLabelLayer.minScale = scale;
        farmerLabelMinScale = scale; // Update the variable for consistency
      }
      
      // Remove existing scale watchers since we're using native ArcGIS scale properties
      if (mapState.taskLabelScaleWatcher) {
        mapState.taskLabelScaleWatcher.remove();
        mapState.taskLabelScaleWatcher = null;
      }
      if (mapState.farmerLabelScaleWatcher) {
        mapState.farmerLabelScaleWatcher.remove();
        mapState.farmerLabelScaleWatcher = null;
      }
      if (mapState.globalLabelScaleWatcher) {
        mapState.globalLabelScaleWatcher.remove();
        mapState.globalLabelScaleWatcher = null;
      }
      
      context.emit('global-label-scale-changed', { scale });
    }

    // Soil data update handler
    function onSoilDataUpdated(data) {
      // Capture legend data for the separate legend component
      if (data && data.chartLegend) {
        soilAnalysisLegendData.value = data.chartLegend;
      }
      
      // Emit event so parent components can react if needed
      context.emit('soil-data-updated', data);
    }

    // Coordinate popup methods
    function showCoordinatePopup(mapPoint) {
      coordinatePopup.latitude = mapPoint.latitude.toFixed(6);
      coordinatePopup.longitude = mapPoint.longitude.toFixed(6);
      coordinatePopup.show = true;
      
      // Add pin marker at clicked location
      addCoordinateMarker(mapPoint);
    }

    function addCoordinateMarker(mapPoint) {
      // Clear any existing coordinate markers
      coordinateMarkerLayer.removeAll();
      
      // Create a small pin marker
      const markerSymbol = new SimpleMarkerSymbol({
        style: "circle",
        color: [255, 0, 0, 0.8], // Red color with slight transparency
        size: "8px",
        outline: {
          color: [255, 255, 255, 1], // White outline
          width: 2
        }
      });
      
      // Create the graphic
      const markerGraphic = new Graphic({
        geometry: mapPoint,
        symbol: markerSymbol
      });
      
      // Add to the coordinate marker layer
      coordinateMarkerLayer.add(markerGraphic);
    }

    function closeCoordinatePopup() {
      coordinatePopup.show = false;
      // Clear the coordinate marker when popup is closed
      coordinateMarkerLayer.removeAll();
    }

    // Created in setupMap once the map view exists
    let argisFeature = null;

    function toggleSnapping(enabled) {
      snappingControl.enabled = enabled;
      argisFeature.setSnapping(enabled);
    }

    async function copyCoordinates() {
      const coordText = `${coordinatePopup.latitude}, ${coordinatePopup.longitude}`;
      try {
        await navigator.clipboard.writeText(coordText);
        // Show visual feedback
        const popup = document.querySelector('.coordinate-popup-content');
        if (popup) {
          popup.style.backgroundColor = '#10b981';
          popup.style.color = 'white';
          setTimeout(() => {
            popup.style.backgroundColor = '';
            popup.style.color = '';
          }, 300);
        }
      } catch (err) {
        console.error('Failed to copy coordinates:', err);
      }
    }

    //array of location to zoom map to
    let locationToZoom = [];
    let featuresLocations = [];
    // Every LPIS layer on the map
    let lpisLayers = [];
    // Drawing snaps to the LPIS parcels: the grey reference parcels and the
    // farmer's own, not the highlight and selection graphics
    const lpisSnapLayers = () => lpisLayers.filter((layer) => layer.type === "feature");
    //function to clear all layer on map
    function clearAllLayerDrawedOnMap(except = []) {
      // List of all layers
      const allLayers = [
        farmIdentifyLayer,
        farmFieldsLayer,
        farmSampleZoneLayer,
        parcelLabelLayer,
        sampleZoneLabelLayer,
        samplePathsLabelLayer,
        smapSamplePathsLayer,
        coordinateMarkerLayer,
        sketchLayer,
        sampleGeoreferencesLayer,
        manualGeoreferencesLabelLayer,
        taskLocationsLayer,
        taskLabelLayer,
        farmerLocationsLayer,
        farmerLabelLayer,
      ];
      
      // Also include farmerLayer which might not be in the list above
      if (typeof farmerLayer !== 'undefined') {
        allLayers.push(farmerLayer);
      }

      // Iterate through all layers
      allLayers.forEach((layer) => {
        if (except.length == 0 || !except.includes(layer)) {
          // Handle FeatureLayer vs GraphicsLayer differently
          if (layer === farmerLocationsLayer && layer.type === 'feature') {
            // For FeatureLayer, clear the source
            layer.source = [];
          } else if (layer.removeAll) {
            // For GraphicsLayer, use removeAll method
            layer.removeAll();
          }
        }
      });
    }



    // Store current sample georeferences for real-time drawing
    let currentSampleGeoreferences = [];
    let currentSampleGraphics = [];

    // Consistent georeference color across the application
    const GEOREFERENCE_COLOR = "#DC2626";

    // Helper function to create dashed line symbol
    function createDashedLineSymbol(color = GEOREFERENCE_COLOR) {
      return new SimpleLineSymbol({
        color: color,
        width: 1, // Match normal sample path line thickness
        style: "dash",
        cap: "round",
        join: "round"
      });
    }

    // Function to create sample point symbol
    function createSamplePointSymbol(color = GEOREFERENCE_COLOR) {
      return new SimpleMarkerSymbol({
        color: color,
        size: 10,
        style: "circle",
        outline: {
          color: "white",
          width: 2
        }
      });
    }

    // Function to create sample label
    function createSampleLabel(sampleId) {
        return new TextSymbol({
        type: "text", // autocasts as new TextSymbol()
        color: "white",
        text: sampleId,
        font: {
          // autocasts as new Font()
          size: 12,
          family: "Arial",
          weight: "bold",
        },
      });
      
    }

    // Store current highlight handles for clearing later
    let currentHighlights = [];
    
    // Function to clear sample selection/highlighting
    function clearSampleSelection() {
      // Don't clear selections during georeference editing mode
      if (isEditingGeoreferenceMarkers) {
        return;
      }
      
      // Remove existing highlights
      currentHighlights.forEach(highlight => {
        if (highlight && highlight.remove) {
          highlight.remove();
        }
      });
      currentHighlights = [];
    }
    
    // Function to zoom to existing SMAP sample path and highlight it
    function zoomToExistingSamplePath(payload) {
      if (!payload.sampleId) return;
      
      // Don't zoom/highlight during georeference editing mode
      if (isEditingGeoreferenceMarkers) {
        return;
      }
      
      // Clear any existing selection first
      clearSampleSelection();
      
      // Find existing sample path graphics that match this sample ID
      const allPathGraphics = smapSamplePathsLayer.graphics.toArray();
      const allLabelGraphics = samplePathsLabelLayer.graphics.toArray();
      
      // Find the sample path graphic (polyline) for this sample
      const samplePathGraphic = allPathGraphics.find(graphic => {
        return graphic.attributes && 
               graphic.geometry && 
               graphic.geometry.type === 'polyline' &&
               (graphic.attributes.sample_id === payload.sampleId ||
                graphic.attributes.sampleId === payload.sampleId ||
                graphic.attributes.task_id === payload.sampleId);
      });
      
      // Find associated markers for this sample
      const sampleMarkers = allPathGraphics.filter(graphic => {
        return graphic.attributes && 
               graphic.geometry && 
               graphic.geometry.type === 'point' &&
               (graphic.attributes.sample_id === payload.sampleId ||
                graphic.attributes.sampleId === payload.sampleId ||
                graphic.attributes.task_id === payload.sampleId);
      });
      
      if (samplePathGraphic) {
        // Use native ArcGIS highlight for SMAP samples
        mapState.view.whenLayerView(smapSamplePathsLayer).then((layerView) => {
          // Collect graphics to highlight (path + markers)
          const graphicsToHighlight = [samplePathGraphic, ...sampleMarkers];
          const objectIds = graphicsToHighlight.map(graphic => graphic.attributes.OBJECTID || graphic.uid).filter(id => id);
          
          if (objectIds.length > 0) {
            const highlight = layerView.highlight(objectIds);
            currentHighlights.push(highlight);
          } else {
            // Fallback: highlight by graphic reference
            graphicsToHighlight.forEach(graphic => {
              const highlight = layerView.highlight(graphic);
              currentHighlights.push(highlight);
            });
          }
        }).catch(error => {
          console.warn('LayerView highlight failed, using graphic reference:', error);
          // Fallback approach
          mapState.view.whenLayerView(smapSamplePathsLayer).then((layerView) => {
            const highlight = layerView.highlight([samplePathGraphic, ...sampleMarkers]);
            currentHighlights.push(highlight);
          });
        });
        
        // Zoom to sample path with proper handling for different geometry types
        let target;
        if (samplePathGraphic.geometry.type === 'point') {
          // For points, go directly to the point with scale limit
          target = {
            target: samplePathGraphic.geometry,
            scale: PATH_FOCUS_SCALE
          };
        } else {
          // For polylines and polygons, use extent and expand
          target = {
            target: samplePathGraphic.geometry.extent.expand(1.1),
            scale: PATH_FOCUS_SCALE
          };
        }
        
        const options = {
          duration: 2000,
          padding: { top: 80, right: 80, bottom: 80, left: 80 }
        };
        
        mapState.view.goTo(target, options).catch(function (error) {
          console.error("Error zooming to sample path:", error);
        });
      } else {
        console.warn("Sample path not found for sample ID:", payload.sampleId);
        
        // Fallback: Try to find path by matching georeference coordinates
        if (payload.georeferences && payload.georeferences.length > 0) {
          const matchingPath = allPathGraphics.find(graphic => {
            if (!graphic.geometry || graphic.geometry.type !== 'polyline') return false;
            
            const paths = graphic.geometry.paths;
            if (!paths || paths.length === 0) return false;
            
            // Check if any of the path coordinates match our georeferences
            const pathCoords = paths[0]; // Get first path
            return payload.georeferences.some(geoRef => {
              return pathCoords.some(coord => {
                const tolerance = 0.001; // Larger tolerance for coordinate matching
                return Math.abs(coord[0] - geoRef.lng) < tolerance && 
                       Math.abs(coord[1] - geoRef.lat) < tolerance;
              });
            });
          });
          
          if (matchingPath) {
            // Highlight the matched path using native ArcGIS highlight
            mapState.view.whenLayerView(smapSamplePathsLayer).then((layerView) => {
              const highlight = layerView.highlight(matchingPath);
              currentHighlights.push(highlight);
            }).catch(error => {
              console.warn('LayerView highlight failed for coordinate match:', error);
            });
            
            // Zoom to matched path with proper handling for different geometry types
            let target;
            if (matchingPath.geometry.type === 'point') {
              // For points, go directly to the point with scale limit
              target = {
                target: matchingPath.geometry,
                scale: PATH_FOCUS_SCALE
              };
            } else {
              // For polylines and polygons, use extent and expand
              target = {
                target: matchingPath.geometry.extent.expand(1.1),
                scale: PATH_FOCUS_SCALE
              };
            }
            
            const options = {
              duration: 2000,
              padding: { top: 80, right: 80, bottom: 80, left: 80 }
            };
            
            mapState.view.goTo(target, options).catch(function (error) {
              console.error("Error zooming to matched sample path:", error);
            });
          } else {
            console.warn('No matching path found by coordinates either');
          }
        }
      }
    }
    
    // Function to update sample georeference visualization (final dashed lines)
    function updateSampleGeoreferenceVisualization(payload) {
      if (!payload.georeferences || payload.georeferences.length === 0) return;

      // Clear any existing graphics for this sample
      clearSampleGeoreferenceVisualization(payload);

      const points = payload.georeferences.map(geo => [geo.lng, geo.lat]);
      const redColor = GEOREFERENCE_COLOR; // Use consistent red color
      
      // Array to store graphics for potential zooming
      const graphicsForZoom = [];
      
      // Create polyline first (so it renders below points)
      if (points.length > 1) {
        const polyline = new Polyline({
          paths: [points]
        });

        const lineGraphic = new Graphic({
          geometry: polyline,
          symbol: createDashedLineSymbol(redColor),
          attributes: {
            sampleId: payload.sampleId,
            sampleIndex: payload.sampleIndex,
            type: "sample-line",
            sampleData: payload.sampleData || {},
            clickable: true
          }
        });

        sampleGeoreferencesLayer.add(lineGraphic);
        graphicsForZoom.push(lineGraphic);
      }
      
      // Create points graphics (renders above lines)
      payload.georeferences.forEach((geo, index) => {
        const point = new Point({
          longitude: geo.lng,
          latitude: geo.lat
        });

        const pointGraphic = new Graphic({
          geometry: point,
          symbol: createSamplePointSymbol(redColor),
          attributes: {
            sampleId: payload.sampleId,
            sampleIndex: payload.sampleIndex,
            type: "sample-point",
            sampleData: payload.sampleData || {},
            clickable: true
          }
        });

        sampleGeoreferencesLayer.add(pointGraphic);
        graphicsForZoom.push(pointGraphic);
      });

      // Add sample label at midpoint of line (or first point if only one point)
      let labelPoint;
      if (points.length > 1) {
        // Calculate midpoint of the line
        const midIndex = Math.floor(points.length / 2);
        if (points.length % 2 === 0) {
          // Even number of points - interpolate between middle two
          const point1 = points[midIndex - 1];
          const point2 = points[midIndex];
          labelPoint = new Point({
            longitude: (point1[0] + point2[0]) / 2,
            latitude: (point1[1] + point2[1]) / 2
          });
        } else {
          // Odd number of points - use middle point
          labelPoint = new Point({
            longitude: points[midIndex][0],
            latitude: points[midIndex][1]
          });
        }
      } else {
        // Single point - place label at that point
        labelPoint = new Point({
          longitude: points[0][0],
          latitude: points[0][1]
        });
      }

      const labelGraphic = new Graphic({
        geometry: labelPoint,
        symbol: createSampleLabel(payload.sampleId),
        attributes: {
          sampleId: payload.sampleId,
          sampleIndex: payload.sampleIndex,
          type: "sample-label",
          sampleData: payload.sampleData || {},
          clickable: true
        }
      });

      manualGeoreferencesLabelLayer.add(labelGraphic);
      
      // Handle zooming and highlighting if requested
      if (payload.zoom && graphicsForZoom.length > 0) {
        // Clear any existing selection first
        clearSampleSelection();
        
        // Use native ArcGIS highlight for manual samples
        mapState.view.whenLayerView(sampleGeoreferencesLayer).then((layerView) => {
          const highlight = layerView.highlight(graphicsForZoom);
          currentHighlights.push(highlight);
        }).catch(error => {
          console.warn('LayerView highlight failed for manual samples:', error);
        });
        
        // Also highlight labels if they exist
        if (manualGeoreferencesLabelLayer.graphics.length > 0) {
          mapState.view.whenLayerView(manualGeoreferencesLabelLayer).then((layerView) => {
            const labelGraphics = manualGeoreferencesLabelLayer.graphics.filter(g => 
              g.attributes && g.attributes.sampleId === payload.sampleId
            ).toArray();
            if (labelGraphics.length > 0) {
              const highlight = layerView.highlight(labelGraphics);
              currentHighlights.push(highlight);
            }
          }).catch(error => {
            console.warn('LayerView highlight failed for manual sample labels:', error);
          });
        }
        
        // Calculate extent from all graphics and expand by 10%
        let combinedGeometries = graphicsForZoom.map(g => g.geometry);
        
        // Handle different geometry types properly
        let targetExtent;
        if (combinedGeometries.length === 1) {
          const geometry = combinedGeometries[0];
          if (geometry.type === 'point') {
            // For single points, go directly to the point
            targetExtent = geometry;
          } else {
            // For polylines and polygons, use extent and expand
            targetExtent = geometry.extent.expand(1.1);
          }
        } else {
          // For multiple geometries, let ArcGIS calculate the extent and then expand
          // We'll pass the graphics array and let goTo calculate, then modify scale
          targetExtent = null; // Will use graphics array directly
        }
        
        const target = targetExtent ? {
          target: targetExtent,
          scale: PATH_FOCUS_SCALE
        } : {
          target: graphicsForZoom,
          scale: PATH_FOCUS_SCALE
        };
        
        const options = {
          duration: 2000,
          padding: { top: 80, right: 80, bottom: 80, left: 80 }
        };
        
        // Handle both single and multiple graphics with smooth animation
        if (targetExtent) {
          // Single geometry or point - go directly
          mapState.view.goTo(target, options).catch(function (error) {
            console.error("Error zooming to sample georeferences:", error);
          });
        } else {
          // Multiple graphics - let ArcGIS calculate extent but apply scale limit
          const multiTarget = {
            target: graphicsForZoom,
            scale: PATH_FOCUS_SCALE
          };
          
          mapState.view.goTo(multiTarget, options).catch(function (error) {
            console.error("Error zooming to sample georeferences:", error);
          });
        }
      }
    }

    // Helper function to create sample popup content
    function createSamplePopupContent(attributes) {
      const sampleData = attributes.sampleData || {};
      const sampleId = attributes.sampleId;
      const sampleIndex = attributes.sampleIndex;
      
      return `
        <div class="sample-popup-content">
          <div class="mb-2">
            <strong>Sample ID:</strong> ${sampleId}
          </div>
          ${sampleData.sample_type ? `
            <div class="mb-2">
              <strong>Type:</strong> ${sampleData.sample_type}
            </div>
          ` : ''}
          ${sampleData.depth_from && sampleData.depth_to ? `
            <div class="mb-2">
              <strong>Depth:</strong> ${sampleData.depth_from} - ${sampleData.depth_to} cm
            </div>
          ` : ''}
          ${sampleData.georeferences && sampleData.georeferences.length ? `
            <div class="mb-2">
              <strong>Points:</strong> ${sampleData.georeferences.length} georeference point(s)
            </div>
          ` : ''}
          <div class="text-sm text-gray-600 mt-2">
            Click and drag to interact with this sample path
          </div>
        </div>
      `;
    }

    // Function to clear specific sample georeference visualization
    function clearSampleGeoreferenceVisualization(payload) {
      
      
      // Clear any temporary graphics first
      currentSampleGraphics.forEach(graphic => {
        sampleGeoreferencesLayer.remove(graphic);
      });
      currentSampleGraphics = [];
      
      // Find and remove all graphics for this sample
      const graphicsToRemove = sampleGeoreferencesLayer.graphics.filter(graphic => {
        const attrs = graphic.attributes;
        return (attrs.sampleId === payload.sampleId) || 
               (attrs.sampleIndex === payload.sampleIndex) ||
               (attrs.type && attrs.type.startsWith('temp-'));
      }).toArray();

      sampleGeoreferencesLayer.removeMany(graphicsToRemove);
    }

    // Function to update real-time georeference drawing during selection
    function updateRealtimeGeoreferenceDrawing(payload) {
      // Clear current temporary graphics
      currentSampleGraphics.forEach(graphic => {
        sampleGeoreferencesLayer.remove(graphic);
      });
      currentSampleGraphics = [];
      
      // Also clear any other temporary graphics that might be left over
      const tempGraphics = sampleGeoreferencesLayer.graphics.filter(graphic => 
        graphic.attributes.type && graphic.attributes.type.startsWith('temp-')
      ).toArray();
      sampleGeoreferencesLayer.removeMany(tempGraphics);

      if (!payload.georeferences || payload.georeferences.length === 0) {
        currentSampleGeoreferences = [];
        return;
      }

      currentSampleGeoreferences = payload.georeferences;
      const points = payload.georeferences.map(geo => [geo.lng, geo.lat]);

      // Draw temporary points (lighter style to indicate they're not final)
      payload.georeferences.forEach((geo, index) => {
        const point = new Point({
          longitude: geo.lng,
          latitude: geo.lat
        });

        const pointGraphic = new Graphic({
          geometry: point,
          symbol: createSamplePointSymbol("#94A3B8"), // Keep gray for temporary points
          attributes: {
            type: "temp-sample-point"
          }
        });

        sampleGeoreferencesLayer.add(pointGraphic);
        currentSampleGraphics.push(pointGraphic);
      });

      // Draw temporary line if more than one point (dotted style)
      if (points.length > 1) {
        const polyline = new Polyline({
          paths: [points]
        });

        const lineSymbol = new SimpleLineSymbol({
          color: "#94A3B8", // Keep gray for temporary dotted lines
          width: 2,
          style: "dot" // dotted style for temporary
        });

        const lineGraphic = new Graphic({
          geometry: polyline,
          symbol: lineSymbol,
          attributes: {
            type: "temp-sample-line"
          }
        });

        sampleGeoreferencesLayer.add(lineGraphic);
        currentSampleGraphics.push(lineGraphic);
      }
    }

    // Track multi-sample graphics separately from single-sample temporary graphics
    let multiSampleGraphics = [];

    // Function to update multi-sample georeference visualization
    function updateMultiSampleGeoreferenceVisualization(payload) {
      if (!payload.georeferences || payload.georeferences.length === 0) return;

      // Clear any existing graphics for this specific sample
      clearSpecificMultiSampleGeoreferences(payload.sampleIndex);

      const points = payload.georeferences.map(geo => [geo.lng, geo.lat]);
      const sampleColor = payload.style?.color || GEOREFERENCE_COLOR;
      const isTemporary = payload.isTemporary || false;
      const sampleId = payload.sampleId || `Sample ${payload.sampleIndex + 1}`;
      
      // Create polyline first (so it renders below points)
      if (points.length > 1) {
        const polyline = new Polyline({
          paths: [points]
        });

        const lineSymbol = new SimpleLineSymbol({
          color: sampleColor,
          width: 1, // Consistent with saved samples
          style: "dash", // Consistent with saved samples
          cap: "round",
          join: "round"
        });

        const lineGraphic = new Graphic({
          geometry: polyline,
          symbol: lineSymbol,
          attributes: {
            sampleId: payload.sampleId,
            sampleIndex: payload.sampleIndex,
            type: "multi-sample-line",
            isTemporary: isTemporary,
            sampleData: payload.sampleData || {},
            clickable: true
          }
        });

        sampleGeoreferencesLayer.add(lineGraphic);
        multiSampleGraphics.push({
          graphic: lineGraphic,
          sampleIndex: payload.sampleIndex,
          type: 'line'
        });
      }
      
      // Create points graphics (renders above lines)
      payload.georeferences.forEach((geo, index) => {
        const point = new Point({
          longitude: geo.lng,
          latitude: geo.lat
        });

        const pointSymbol = new SimpleMarkerSymbol({
          color: sampleColor,
          size: 10, // Same as original createSamplePointSymbol
          style: "circle",
          outline: {
            color: "white",
            width: 2
          }
        });

        const pointGraphic = new Graphic({
          geometry: point,
          symbol: pointSymbol,
          attributes: {
            sampleId: payload.sampleId,
            sampleIndex: payload.sampleIndex,
            type: "multi-sample-point",
            isTemporary: isTemporary,
            pointIndex: index,
            sampleData: payload.sampleData || {},
            clickable: true
          }
        });

        sampleGeoreferencesLayer.add(pointGraphic);
        multiSampleGraphics.push({
          graphic: pointGraphic,
          sampleIndex: payload.sampleIndex,
          type: 'point'
        });
      });

      // Add sample label at midpoint of line (or first point if only one point)
      let labelPoint;
      if (points.length > 1) {
        // Calculate midpoint of the line
        const midIndex = Math.floor(points.length / 2);
        if (points.length % 2 === 0) {
          // Even number of points - interpolate between middle two
          const point1 = points[midIndex - 1];
          const point2 = points[midIndex];
          labelPoint = new Point({
            longitude: (point1[0] + point2[0]) / 2,
            latitude: (point1[1] + point2[1]) / 2
          });
        } else {
          // Odd number of points - use middle point
          labelPoint = new Point({
            longitude: points[midIndex][0],
            latitude: points[midIndex][1]
          });
        }
      } else {
        // Single point - place label at that point with offset
        labelPoint = new Point({
          longitude: points[0][0],
          latitude: points[0][1]
        });
      }

      const labelSymbol = new TextSymbol({
        color: "white",
        text: sampleId,
        font: {
          size: 10,
          family: "Arial",
          weight: "bold"
        },
        haloColor: "black", // Use black halo like saved samples
        haloSize: 1,
        verticalAlignment: "bottom",
        yoffset: 8 // Same as original createSampleLabel
      });

      const labelGraphic = new Graphic({
        geometry: labelPoint,
        symbol: labelSymbol,
        attributes: {
          sampleId: payload.sampleId,
          sampleIndex: payload.sampleIndex,
          type: "multi-sample-label",
          isTemporary: isTemporary,
          sampleData: payload.sampleData || {},
          clickable: true
        }
      });

      manualGeoreferencesLabelLayer.add(labelGraphic);
      multiSampleGraphics.push({
        graphic: labelGraphic,
        sampleIndex: payload.sampleIndex,
        type: 'label'
      });
    }

    // Function to clear all multi-sample georeferences
    function clearAllMultiSampleGeoreferences() {
      
      // Remove all multi-sample graphics from the layers
      multiSampleGraphics.forEach(item => {
        if (item.type === 'label') {
          // Labels are on the manualGeoreferencesLabelLayer
          manualGeoreferencesLabelLayer.remove(item.graphic);
        } else {
          // Points and lines are on the sampleGeoreferencesLayer
          sampleGeoreferencesLayer.remove(item.graphic);
        }
      });
      
      // Clear the tracking array
      multiSampleGraphics = [];
      
      // Also clear any old single-sample temporary graphics that might still be around
      currentSampleGraphics.forEach(graphic => {
        sampleGeoreferencesLayer.remove(graphic);
      });
      currentSampleGraphics = [];
      
      // Nuclear option: clear ALL graphics from both layers to ensure nothing is left behind
      sampleGeoreferencesLayer.removeAll();
      manualGeoreferencesLabelLayer.removeAll();
      
      // Also clear sketch layer in case there are any temporary graphics there
      sketchLayer.removeAll();
    }

    // Function to clear georeferences for a specific sample
    function clearSpecificMultiSampleGeoreferences(sampleIndex) {
      // Find and remove graphics for this specific sample
      const graphicsToRemove = multiSampleGraphics.filter(item => 
        item.sampleIndex === sampleIndex
      );
      
      graphicsToRemove.forEach(item => {
        if (item.type === 'label') {
          // Labels are on the manualGeoreferencesLabelLayer
          manualGeoreferencesLabelLayer.remove(item.graphic);
        } else {
          // Points and lines are on the sampleGeoreferencesLayer
          sampleGeoreferencesLayer.remove(item.graphic);
        }
      });
      
      // Update the tracking array to remove cleared graphics
      multiSampleGraphics = multiSampleGraphics.filter(item => 
        item.sampleIndex !== sampleIndex
      );
    }

    // Functions to manage popup templates on graphics
    function disablePopupTemplates() {
      const layers = [smapSamplePathsLayer, sampleGeoreferencesLayer];
      layers.forEach(layer => {
        layer.graphics.forEach(graphic => {
          if (graphic.popupTemplate) {
            graphic._originalPopupTemplate = graphic.popupTemplate; // Store original
            graphic.popupTemplate = null; // Disable
          }
        });
      });
    }

    function enablePopupTemplates() {
      const layers = [smapSamplePathsLayer, sampleGeoreferencesLayer];
      layers.forEach(layer => {
        layer.graphics.forEach(graphic => {
          if (graphic._originalPopupTemplate) {
            graphic.popupTemplate = graphic._originalPopupTemplate; // Restore original
            delete graphic._originalPopupTemplate; // Clean up
          }
        });
      });
    }

    // GPS Marker Selection Functions
    let gpsMarkerGraphics = [];
    let currentGPSPath = null;
    let currentGPSSample = null;
    
    // Track if user is currently editing any type of georeference markers
    let isEditingGeoreferenceMarkers = false;

    function startGPSMarkerSelectionMode(payload) {
      // Set editing state
      isEditingGeoreferenceMarkers = true;
      
      // Disable ArcGIS built-in popup and selection systems
      if (mapState.view && mapState.view.popup) {
        if (typeof mapState.view.popup.close === 'function') {
          mapState.view.popup.close(); // Close any existing popup
        }
        mapState.view.popup.autoOpenEnabled = false;
        if (mapState.view.popup.hasOwnProperty('visible')) {
          mapState.view.popup.visible = false;
        }
      }
      
      // Disable popup templates on graphics
      disablePopupTemplates();
      if (mapState.view) {
        // Store original highlight color and disable highlighting
        if (!mapState.view._originalHighlightColor) {
          mapState.view._originalHighlightColor = mapState.view.highlightOptions.color;
        }
        mapState.view.highlightOptions = { color: "transparent" };
      }
      
      // Store current sample info
      currentGPSSample = {
        sampleId: payload.sampleId,
        pathCoordinates: payload.pathCoordinates,
        currentMarkers: payload.currentMarkers || []
      };

      // Clear any existing GPS marker graphics
      clearGPSMarkerGraphics();

      // Render GPS path and interactive points
      renderGPSPathAndPoints(payload);
      
      // Zoom to the GPS sample path
      if (currentGPSPath && mapState.view) {
        const target = {
          target: currentGPSPath.geometry.extent.expand(1.2),
          scale: PATH_FOCUS_SCALE
        };
        
        const options = {
          duration: 1500,
          padding: { top: 80, right: 80, bottom: 80, left: 80 }
        };
        
        mapState.view.goTo(target, options).catch(function (error) {
          console.warn("Error zooming to GPS sample:", error);
        });
      }
    }

    function stopGPSMarkerSelectionMode(payload) {
      // Clear GPS marker graphics
      clearGPSMarkerGraphics();
      
      // Reset current sample info
      currentGPSSample = null;
      currentGPSPath = null;
      
      // Clear editing state
      isEditingGeoreferenceMarkers = false;
      
      // Re-enable ArcGIS built-in popup and selection systems
      if (mapState.view && mapState.view.popup) {
        mapState.view.popup.autoOpenEnabled = true;
        if (mapState.view.popup.hasOwnProperty('visible')) {
          mapState.view.popup.visible = true;
        }
      }
      
      // Re-enable popup templates on graphics
      enablePopupTemplates();
      if (mapState.view) {
        // Restore original highlight color
        if (mapState.view._originalHighlightColor) {
          mapState.view.highlightOptions = { color: mapState.view._originalHighlightColor };
        } else {
          mapState.view.highlightOptions = { color: [0, 255, 255, 1] }; // Default cyan/blue
        }
      }
    }

    function renderGPSPathAndPoints(payload) {
      if (!payload.pathCoordinates || !payload.pathCoordinates[0]) return;

      const paths = payload.pathCoordinates[0];
      const currentMarkers = payload.currentMarkers || [];

      // Create GPS path line
      const pathPoints = paths.map(coord => [coord[0], coord[1]]); // [lng, lat] format

      const pathPolyline = new Polyline({
        paths: [pathPoints],
        spatialReference: { wkid: 4326 }
      });

      const pathSymbol = new SimpleLineSymbol({
        color: [59, 130, 246, 0.8], // Blue color
        width: 3,
        style: "solid"
      });

      currentGPSPath = new Graphic({
        geometry: pathPolyline,
        symbol: pathSymbol,
        attributes: {
          type: "gps-path",
          sampleId: payload.sampleId
        }
      });

      sampleGeoreferencesLayer.add(currentGPSPath);

      // Create interactive points along the GPS path
      paths.forEach((coord, index) => {
        const [lng, lat] = coord;
        
        const point = new Point({
          longitude: lng,
          latitude: lat,
          spatialReference: { wkid: 4326 }
        });

        const isSelected = currentMarkers.includes(index);

        const pointSymbol = new SimpleMarkerSymbol({
          color: isSelected ? [239, 68, 68, 0.9] : [156, 163, 175, 0.6], // Red if selected, gray if not
          size: isSelected ? 12 : 8,
          style: "circle",
          outline: {
            color: "white",
            width: 2
          }
        });

        const pointGraphic = new Graphic({
          geometry: point,
          symbol: pointSymbol,
          attributes: {
            type: "gps-marker-point",
            pointIndex: index,
            isSelected: isSelected,
            sampleId: payload.sampleId,
            clickable: true
          }
        });

        sampleGeoreferencesLayer.add(pointGraphic);
        gpsMarkerGraphics.push(pointGraphic);
      });
    }

    function updateGPSMarkerSelectionVisual(payload) {
      if (!gpsMarkerGraphics.length) return;

      // Update visual appearance of all GPS marker points
      gpsMarkerGraphics.forEach(graphic => {
        const pointIndex = graphic.attributes.pointIndex;
        const isSelected = payload.selectedMarkers.includes(pointIndex);
        
        // Update symbol
        const pointSymbol = new SimpleMarkerSymbol({
          color: isSelected ? [239, 68, 68, 0.9] : [156, 163, 175, 0.6], // Red if selected, gray if not
          size: isSelected ? 12 : 8,
          style: "circle",
          outline: {
            color: "white",
            width: 2
          }
        });

        graphic.symbol = pointSymbol;
        graphic.attributes.isSelected = isSelected;
      });
    }

    function clearGPSMarkerGraphics() {
      // Remove GPS path graphic
      if (currentGPSPath) {
        sampleGeoreferencesLayer.remove(currentGPSPath);
        currentGPSPath = null;
      }

      // Remove all GPS marker point graphics
      gpsMarkerGraphics.forEach(graphic => {
        sampleGeoreferencesLayer.remove(graphic);
      });
      gpsMarkerGraphics = [];
    }

    // Function to display task location pins - minimum viable solution
    // Helper function to get pin URL based on task status - matches dashboard card colors exactly
    function getTaskPinUrl(status, statusClass) {
      // Use status_class from backend to determine color - matches dashboard cards
      if (statusClass) {
        if (statusClass.includes('bg-gray-100')) return '/img/widget/icon-pin-red@40x40.svg'; // Red for not_started (dashboard shows red-700)
        if (statusClass.includes('bg-blue-100')) return '/img/widget/icon-pin-amber@40x40.svg'; // Amber for in_progress (dashboard shows amber-700)
        if (statusClass.includes('bg-yellow-100')) return '/img/widget/icon-pin-blue@40x40.svg'; // Blue for collected (dashboard shows blue-700)
        if (statusClass.includes('bg-indigo-100')) return '/img/widget/icon-pin-purple@40x40.svg'; // Purple for lab (dashboard shows purple-700)
        if (statusClass.includes('bg-green-100')) return '/img/widget/icon-pin-emerald@40x40.svg'; // Emerald for complete (dashboard shows emerald-700)
        if (statusClass.includes('bg-red-100')) return '/img/widget/icon-pin-red@40x40.svg'; // Red for rejected
        if (statusClass.includes('bg-slate-200')) return '/img/widget/icon-pin-slate@40x40.svg'; // Slate for cancelled
      }
      
      // Fallback based on status text - matches dashboard card colors exactly
      switch (status) {
        case 'not_started': return '/img/widget/icon-pin-red@40x40.svg'; // Red like dashboard
        case 'in_progress': return '/img/widget/icon-pin-amber@40x40.svg'; // Amber like dashboard
        case 'collected': return '/img/widget/icon-pin-blue@40x40.svg'; // Blue like dashboard
        case 'lab': return '/img/widget/icon-pin-purple@40x40.svg'; // Purple like dashboard
        case 'complete': return '/img/widget/icon-pin-emerald@40x40.svg'; // Emerald like dashboard
        case 'rejected': return '/img/widget/icon-pin-red@40x40.svg'; // Red
        case 'cancelled': return '/img/widget/icon-pin-slate@40x40.svg'; // Slate
        default: return '/img/widget/icon-pin-red@40x40.svg'; // Default red like not_started
      }
    }

    function displayTaskLocations(tasks) {
      
      // Clear any existing task pins and labels
      taskLocationsLayer.removeAll();
      taskLabelLayer.removeAll();
      
      // Only clear farmer pins when showing tasks (not when clearing tasks)
      if (tasks && tasks.length > 0) {
        // Clear FeatureLayer source instead of calling removeAll
        farmerLocationsLayer.source = [];
        farmerLabelLayer.removeAll();
      }
      
      // Clean up existing scale watchers
      if (mapState.taskLabelScaleWatcher) {
        mapState.taskLabelScaleWatcher.remove();
        mapState.taskLabelScaleWatcher = null;
      }
      if (mapState.farmerLabelScaleWatcher) {
        mapState.farmerLabelScaleWatcher.remove();
        mapState.farmerLabelScaleWatcher = null;
      }
      
      const taskPoints = []; // Collect all task points for extent calculation
      const uniqueLabels = new Map(); // Track unique farmer labels by location
      
      tasks.forEach(task => {
        if (task.farm_location && task.farm_location.length >= 2) {
          // Handle coordinate order inconsistency - some farms have lat/lon swapped
          let longitude = task.farm_location[0];
          let latitude = task.farm_location[1];
          
          // For Ireland, longitude should be negative (-10 to -6) and latitude positive (51 to 55)
          // If first coordinate is positive and in Ireland lat range, coordinates are swapped
          if (longitude > 0 && longitude >= 51 && longitude <= 55 && latitude < 0 && latitude >= -10 && latitude <= -6) {
            [longitude, latitude] = [latitude, longitude]; // Swap them
          }
                   
          const point = new Point({
            longitude: longitude,
            latitude: latitude,
            spatialReference: { wkid: 3857 }
          });

          taskPoints.push(point); // Add to points collection for extent

          // Use color-coded pin based on task status, same style as farm pins
          const pinUrl = getTaskPinUrl(task.status, task.status_class);
          const symbol = new PictureMarkerSymbol({
            url: pinUrl,
            width: "30px",
            height: "30px",
          });

          const graphic = new Graphic({
            geometry: point,
            symbol: symbol,
            attributes: {
              task_id: task.id,
              farmer_name: task.farmer_name,
              farm_name: task.farm_name,
              task_number: task.task_number,
              status: task.status
            },
            popupTemplate: {
              title: task.farmer_name + (task.farm_name ? ` - ${task.farm_name}` : ''),
              content: `
                <div>
                  <strong>Farmer:</strong> ${task.farmer_name}<br>
                  ${task.farm_name ? `<strong>Farm:</strong> ${task.farm_name}<br>` : ''}
                  <strong>Task #:</strong> ${task.task_number}<br>
                  <strong>Status:</strong> ${task.status}
                </div>
              `
            }
          });

          taskLocationsLayer.add(graphic);

          // Create unique key for farmer label (farmer name + location)
          const labelKey = `${task.farmer_name}_${longitude}_${latitude}`;
          
          // Only create one label per unique farmer location
          if (!uniqueLabels.has(labelKey)) {
            const labelText = task.farmer_name || `Task ${task.task_number}`;
            const labelSymbol = new TextSymbol({
              color: "white",
              text: labelText,
              font: {
                size: 14,
                family: "Arial",
                weight: "lighter"
              },
              haloColor: "black",
              haloSize: 1,
              xoffset: 0,
              yoffset: -24
            });

            const labelGraphic = new Graphic({
              geometry: point,
              symbol: labelSymbol,
              attributes: {
                farmer_name: task.farmer_name,
                label_text: labelText,
                location_key: labelKey
              }
            });

            taskLabelLayer.add(labelGraphic);
            uniqueLabels.set(labelKey, labelGraphic);
          }
        }
      });

      // Add scale-based visibility for labels - hide when scale > taskLabelMinScale (zoomed out)
      const updateLabelVisibility = () => {
        const currentScale = mapState.view.scale;
        const showLabels = currentScale <= taskLabelMinScale;
        taskLabelLayer.visible = showLabels;
      };

      // Set initial visibility
      updateLabelVisibility();

      // Watch for scale changes to show/hide labels
      if (mapState.view.watch && !mapState.taskLabelScaleWatcher) {
        mapState.taskLabelScaleWatcher = mapState.view.watch('scale', updateLabelVisibility);
      }

      // Zoom to the extent of all task locations
      if (taskPoints.length > 0) {
        mapState.view.when(() => {
          // Get all graphics from the task locations layer
          const taskGraphics = taskLocationsLayer.graphics.toArray();

          if (taskGraphics.length > 0) {
            // Create extent from all task points
            let xmin = Infinity, ymin = Infinity, xmax = -Infinity, ymax = -Infinity;
            
            taskGraphics.forEach(graphic => {
              const point = graphic.geometry;
              // Use x,y properties for Web Mercator coordinates (wkid: 3857)
              xmin = Math.min(xmin, point.x);
              ymin = Math.min(ymin, point.y);
              xmax = Math.max(xmax, point.x);
              ymax = Math.max(ymax, point.y);
            });
                        
            // Create extent and expand it
            const extent = new Extent({
              xmin: xmin,
              ymin: ymin,
              xmax: xmax,
              ymax: ymax,
              spatialReference: { wkid: 3857 }
            });
            
            const expandedExtent = extent.expand(1.5);
            
            mapState.view.goTo(expandedExtent, {
              animate: true,
              duration: 1000
            }).catch(error => {
              console.error('Error zooming to task graphics:', error);
            });
          }
        }).catch(error => {
          console.error('Error with mapState.view.when():', error);
        });
      }
    }

    // Function to display farmer location pins using FeatureLayer with native clustering
    function displayFarmerLocations(farmers) {
      // Clear any existing farmer labels
      farmerLabelLayer.removeAll();
      
      // Only clear task pins when showing farmers (not when clearing farmers)
      if (farmers && farmers.length > 0) {
        taskLocationsLayer.removeAll();
        taskLabelLayer.removeAll();
      }
      
      // Clean up existing scale watchers
      if (mapState.farmerLabelScaleWatcher) {
        mapState.farmerLabelScaleWatcher.remove();
        mapState.farmerLabelScaleWatcher = null;
      }
      if (mapState.taskLabelScaleWatcher) {
        mapState.taskLabelScaleWatcher.remove();
        mapState.taskLabelScaleWatcher = null;
      }
      
      // Create features for the FeatureLayer
      const features = [];
      const farmerPoints = []; // For extent calculation
      let objectId = 1;
      
      farmers.forEach(farmer => {
        if (farmer.farm_location && farmer.farm_location.length >= 2) {
          // Handle coordinate order inconsistency - some farms have lat/lon swapped
          let longitude = farmer.farm_location[0];
          let latitude = farmer.farm_location[1];
          
          // For Ireland, longitude should be negative (-10 to -6) and latitude positive (51 to 55)
          // If first coordinate is positive and in Ireland lat range, coordinates are swapped
          if (longitude > 0 && longitude >= 51 && longitude <= 55 && latitude < 0 && latitude >= -10 && latitude <= -6) {
            [longitude, latitude] = [latitude, longitude]; // Swap them
          }
          
          const point = new Point({
            longitude: longitude,
            latitude: latitude,
            spatialReference: { wkid: 3857 }
          });

          farmerPoints.push(point); // Add to points collection for extent

          // Create Graphic for FeatureLayer source
          const graphic = new Graphic({
            geometry: point,
            attributes: {
              ObjectID: objectId++,
              farmer_id: farmer.id?.toString() || '',
              farmer_name: farmer.name || '',
              farm_name: farmer.farm_name || '',
              advisor: farmer.advisor || '',
              address: farmer.address || 'Not Set'
            }
          });
          
          
          features.push(graphic);

          // Still create labels separately for now (could be enhanced later)
          const labelText = farmer.name;
          const labelSymbol = new TextSymbol({
            color: "white",
            text: labelText,
            font: {
              size: 14,
              family: "Arial",
              weight: "lighter"
            },
            haloColor: "black",
            haloSize: 1,
            xoffset: 0,
            yoffset: -24
          });

          const labelGraphic = new Graphic({
            geometry: point,
            symbol: labelSymbol,
            attributes: {
              farmer_id: farmer.id,
              label_text: labelText
            }
          });

          farmerLabelLayer.add(labelGraphic);
        }
      });
      
      // Wait for layer to load before setting source
      if (farmerLocationsLayer.loaded) {
        farmerLocationsLayer.source = features;
        console.log('Source set immediately - layer already loaded');
      } else {
        farmerLocationsLayer.load().then(() => {
          farmerLocationsLayer.source = features;
        }).catch((error) => {
          console.error('Failed to load FeatureLayer:', error);
        });
      }

      // Add scale-based visibility for labels - hide when scale > taskLabelMinScale (zoomed out)
      const updateLabelVisibility = () => {
        const currentScale = mapState.view.scale;
        const showLabels = currentScale <= taskLabelMinScale;
        farmerLabelLayer.visible = showLabels;
      };

      // Set initial visibility
      updateLabelVisibility();

      // Watch for scale changes to show/hide labels
      if (mapState.view.watch && !mapState.farmerLabelScaleWatcher) {
        mapState.farmerLabelScaleWatcher = mapState.view.watch('scale', updateLabelVisibility);
      }

      // Zoom to the extent of all farmer locations
      if (farmerPoints.length > 0) {
        mapState.view.when(() => {
          // Create extent from all farmer points
          let xmin = Infinity, ymin = Infinity, xmax = -Infinity, ymax = -Infinity;
          
          farmerPoints.forEach(point => {
            xmin = Math.min(xmin, point.x);
            ymin = Math.min(ymin, point.y);
            xmax = Math.max(xmax, point.x);
            ymax = Math.max(ymax, point.y);
          });
          
          // Create extent and expand it
          const extent = new Extent({
            xmin: xmin,
            ymin: ymin,
            xmax: xmax,
            ymax: ymax,
            spatialReference: { wkid: 3857 }
          });
          
          const expandedExtent = extent.expand(1.5);
          
          mapState.view.goTo(expandedExtent, {
            animate: true,
            duration: 1000
          }).catch(error => {
            console.error('Error zooming to farmer locations:', error);
          });
        }).catch(error => {
          console.error('Error with mapState.view.when():', error);
        });
      }
    }

    const setupMap = () => {
      // esriConfig.apiKey = apikey;

      //intialize the map

      const composableMap = useMapView(apikey, "viewDiv", center, zoom);
      mapState.view = composableMap.view;
      mapState.map = composableMap.map;

      //listener for zoomTolocationEmitted
      $emitter.on("zoomTolocationEmitted", (action) => {
        mapState.view.when(() => {
          if (action) {
            zoomToLocation(action);
          }
        });
      });

      //listener for add farm point which activate the dot picker of the map
      $emitter.on("addFarmPoint", (action) => {

        argisFeature.activeSketchTool(
          {
            sketchLayer: sketchLayer,
            farmFieldsLayer: farmFieldsLayer,
            snapLayers: lpisSnapLayers(),
            view: mapState.view,
            usedParcelIds: action.usedParcelIds, // Forward parcel availability data
          },
          $emitter,
          action
        );

        snappingControl.show = action.action === "parcel-and-zone";
      });

      // listener for a polygon or point drawing being finished
      $emitter.on("sketchComplete", () => {
        snappingControl.show = false;
      });

      // listener for cancel all draw interaction on the map
      $emitter.on("cancelFarmInteraction", () => {
        snappingControl.show = false;
        argisFeature.sketchCancel();
        // Don't clear all layers when cancelling editing - sketchCancel already restores original graphics
        // Only clear sketch layer and temporary editing graphics
        sketchLayer.removeAll();
      });

      // listener for complete and close all unfinish drawing
      $emitter.on("completeAllEditing", () => {
        snappingControl.show = false;
        argisFeature.sketchHandlerUpdate($emitter);
      });

      // Listener for updating sample georeferences (dashed lines)
      $emitter.on("updateSampleGeoreferences", (payload) => {
        updateSampleGeoreferenceVisualization(payload);
      });

      // Listener for zooming to existing SMAP sample paths
      $emitter.on("zoomToExistingSamplePath", (payload) => {
        zoomToExistingSamplePath(payload);
      });

      // Listener for clearing specific sample georeferences
      $emitter.on("clearSampleGeoreferences", (payload) => {
        clearSampleGeoreferenceVisualization(payload);
      });

      // Listener for real-time georeference drawing during selection
      $emitter.on("updateGeoreferences", (payload) => {
        // Set editing state when georeference selection starts
        isEditingGeoreferenceMarkers = true;
        
        // Disable ArcGIS built-in popup and selection systems during georeference editing
        if (mapState.view && mapState.view.popup) {
          if (typeof mapState.view.popup.close === 'function') {
            mapState.view.popup.close(); // Close any existing popup
          }
          mapState.view.popup.autoOpenEnabled = false;
          if (mapState.view.popup.hasOwnProperty('visible')) {
            mapState.view.popup.visible = false;
          }
        }
        
        // Disable popup templates on graphics
        disablePopupTemplates();
        if (mapState.view) {
          // Store original highlight color and disable highlighting
          if (!mapState.view._originalHighlightColor) {
            mapState.view._originalHighlightColor = mapState.view.highlightOptions.color;
          }
          mapState.view.highlightOptions = { color: "transparent" };
        }
        updateRealtimeGeoreferenceDrawing(payload);
      });

      // Listener for multi-sample georeference visualization
      $emitter.on("updateMultiSampleGeoreferences", (payload) => {
        updateMultiSampleGeoreferenceVisualization(payload);
      });

      // Listener for clearing all multi-sample georeferences
      $emitter.on("clearAllMultiSampleGeoreferences", () => {
        clearAllMultiSampleGeoreferences();
      });

      // Listener for stopping georeference selection - also clear any remaining temporary graphics
      $emitter.on("stopGeoreferenceSelection", () => {
        // Clear editing state
        isEditingGeoreferenceMarkers = false;
        
        // Re-enable ArcGIS built-in popup and selection systems
        if (mapState.view && mapState.view.popup) {
          mapState.view.popup.autoOpenEnabled = true;
          if (mapState.view.popup.hasOwnProperty('visible')) {
            mapState.view.popup.visible = true;
          }
        }
        
        // Re-enable popup templates on graphics
        enablePopupTemplates();
        if (mapState.view) {
          // Restore original highlight color
          if (mapState.view._originalHighlightColor) {
            mapState.view.highlightOptions = { color: mapState.view._originalHighlightColor };
          } else {
            mapState.view.highlightOptions = { color: [0, 255, 255, 1] }; // Default cyan/blue
          }
        }
        
        // Clear any temporary graphics that might still be around
        currentSampleGraphics.forEach(graphic => {
          sampleGeoreferencesLayer.remove(graphic);
        });
        currentSampleGraphics = [];
        
        // Clear any temp graphics by type filter
        const tempGraphics = sampleGeoreferencesLayer.graphics.filter(graphic => 
          graphic.attributes.type && graphic.attributes.type.startsWith('temp-')
        ).toArray();
        sampleGeoreferencesLayer.removeMany(tempGraphics);
      });

      // GPS Marker Selection Event Listeners
      $emitter.on("startGPSMarkerSelection", (payload) => {
        startGPSMarkerSelectionMode(payload);
      });

      $emitter.on("stopGPSMarkerSelection", (payload) => {
        stopGPSMarkerSelectionMode(payload);
      });

      $emitter.on("updateGPSMarkerSelection", (payload) => {
        updateGPSMarkerSelectionVisual(payload);
      });

      // Task Locations Event Listener - minimum viable solution
      $emitter.on("showTaskLocations", (payload) => {
        displayTaskLocations(payload.tasks);
      });

      // Farmer Locations Event Listener 
      $emitter.on("showFarmerLocations", (payload) => {
        displayFarmerLocations(payload.farmers);
      });
      
      // Layer visibility control events
      $emitter.on("hideTaskPins", () => {
        taskLocationsLayer.visible = false;
        taskLabelLayer.visible = false;
      });
      
      $emitter.on("showTaskPins", () => {
        taskLocationsLayer.visible = true;
        taskLabelLayer.visible = true;
      });
      
      $emitter.on("hideFarmerPins", () => {
        farmerLocationsLayer.visible = false;
        farmerLabelLayer.visible = false;
      });
      
      $emitter.on("showFarmerPins", () => {
        farmerLocationsLayer.visible = true;
        farmerLabelLayer.visible = true;
      });

      // Get the element by its ID

      var dropOff;
      var samplerLocation;

      //toggle to show completed herd number for isec

      //add drop off points to map
      watch(dropoff_points, (newDropoffPoints, oldDropoffPoints) => {
        if (newDropoffPoints === oldDropoffPoints) return;
        
        if (newDropoffPoints) {
          // Create a Drop Off Points
          dropOff = createDropoffPointsLayer();
          mapState.map.add(dropOff);
        } else if (dropOff) {
          mapState.map.remove(dropOff);
        }
      });

      // add sampler location layer to map
      watch(sampler_location, (newSamplerLocation, oldSamplerLocation) => {
        if (newSamplerLocation === oldSamplerLocation) return;
        
        if (newSamplerLocation) {
          // Create a sample locations Points
          samplerLocation = createSamplerLocationLayer();
          mapState.map.add(samplerLocation);
        } else if (samplerLocation) {
          mapState.map.remove(samplerLocation);
        }
      });

      //watch for farmer change
      // add arcgis feature to the new widget
      argisFeature = new ArcgisFeature(mapState.view);

      watch(farmer, (newFarmer, oldFarmer) => {
        // Prevent unnecessary re-execution
        if (newFarmer === oldFarmer) return;
        
        // Check if there are existing sample paths that should be preserved (e.g., from task view with showPaths parameter)
        const rootData = internalInstance?.root?.data;
        const shouldPreservePaths = rootData?.paths?.samples && rootData.paths.samples.length > 0;
        
        if (!shouldPreservePaths) {
          //remove all drawed on smap sample paths layer
          clearAllLayerDrawedOnMap([smapSamplePathsLayer, samplePathsLabelLayer]);
        } else {
          // Force paths watcher to re-render preserved paths
          const currentPaths = rootData?.paths?.samples;
          if (currentPaths && currentPaths.length > 0) {
            // Clear layers first
            smapSamplePathsLayer.removeAll();
            samplePathsLabelLayer.removeAll();
            // Re-render the paths
            currentPaths.forEach((path) => {
              if (!path.paths_xy || path.paths_xy.length < 1) return;
              const { samplePath, sampleLabel, georeferenceMarkers } = createPathsGraphic(path);
              
              if (samplePath) {
                smapSamplePathsLayer.add(samplePath);
              }
              
              if (sampleLabel) {
                samplePathsLabelLayer.add(sampleLabel);
              }
              
              if (georeferenceMarkers && georeferenceMarkers.length > 0) {
                georeferenceMarkers.forEach(marker => {
                  if (marker) {
                    smapSamplePathsLayer.add(marker);
                  }
                });
              }
            });
          }
        }

        // Only zoom to LPIS features if explicitly allowed and zoom is not disabled
        // Use safer access to avoid reactive dependencies
        if (
          rootData?.Lpis?.show == "all" &&
          !rootData?.farmer?.hasOwnProperty("allowToLocationOnMap") &&
          newFarmer?.zoom !== false
        ) {
          zoomToLocation(featuresLocations);
        }

        if (argisFeature && newFarmer && newFarmer.hasOwnProperty('name') && newFarmer.hasOwnProperty('hashid')) {
          
          argisFeature.switchMapView(newFarmer, {
            farmerLayer,
            parcelLabelLayer,
            sampleZoneLabelLayer,
            farmIdentifyLayer,
            farmFieldsLayer,
            farmSampleZoneLayer,
            smapSamplePathsLayer,
          });

          // Set initial layer visibility state (labels controlled by scale properties)
          argisFeature.manageLayerVisibility("fields", {
            farmIdentifyLayer,
            farmFieldsLayer,
            farmSampleZoneLayer,
            parcelLabelLayer,
            sampleZoneLabelLayer,
            farmerLayer,
            smapSamplePathsLayer,
          });
        } else if (newFarmer) {
          // Handle farmer filter/list data - clear individual farmer layers
          clearAllLayerDrawedOnMap([farmIdentifyLayer, farmFieldsLayer, farmSampleZoneLayer, smapSamplePathsLayer, samplePathsLabelLayer]);
        } else {
          // No valid farmer data - clear all layers
          clearAllLayerDrawedOnMap([]);
        }
      });

      // Add click event handling for sample georeference graphics and parcel selection
      mapState.view.on("click", (event) => {
        // LPIS linking owns map clicks until it is saved or cancelled
        if (isLpisLinking()) {
          return;
        }

        // First check if we're in parcel selection mode
        if (argisFeature.activeAction === "parcel-georeference-selection") {
          // Perform hitTest for parcel selection
          const parcelOpts = {
            include: farmFieldsLayer
          };

          mapState.view.hitTest(event, parcelOpts).then((response) => {
            if (response.results.length > 0) {
              const graphic = response.results[0].graphic;
              argisFeature.handleParcelClick(graphic, event.mapPoint);
            }
            // If no parcel clicked, do nothing (user can keep clicking until they hit a parcel)
          }).catch((error) => {
            console.error("Error performing hitTest for parcel selection:", error);
          });
          
          return; // Don't process other click handlers when in parcel selection mode
        }

        // Handle georeference editing mode vs normal sample georeference clicks
        if (isEditingGeoreferenceMarkers) {
          // GPS marker selection mode: only handle GPS marker clicks
          const opts = {
            include: sampleGeoreferencesLayer
          };
          
          mapState.view.hitTest(event, opts).then((response) => {
            if (response.results.length > 0) {
              const graphic = response.results[0].graphic;
              const attrs = graphic.attributes;
              
              // Only handle GPS marker point clicks
              if (attrs.type === "gps-marker-point" && attrs.clickable) {
                $emitter.emit("gpsMarkerClick", {
                  pointIndex: attrs.pointIndex,
                  sampleId: attrs.sampleId,
                  isSelected: attrs.isSelected
                });
              }
            }
          }).catch((error) => {
            console.error("Error performing hitTest for GPS markers:", error);
          });
          
          return; // Skip all other click handling in GPS mode
        }
        
        // Normal mode: handle sample georeference clicks with popups
        const opts = {
          include: sampleGeoreferencesLayer
        };

        mapState.view.hitTest(event, opts).then((response) => {
          if (response.results.length > 0) {
            const graphic = response.results[0].graphic;
            const attrs = graphic.attributes;
            
            // Handle regular sample georeference clicks (only when NOT in georeference editing mode)
            if (attrs.clickable && attrs.sampleId && !isEditingGeoreferenceMarkers) {
              // Create popup content with sample details
              const popupContent = createSamplePopupContent(attrs);
              
              mapState.view.popup.open({
                title: `Sample: ${attrs.sampleId}`,
                content: popupContent,
                location: event.mapPoint
              });
            }
          }
        }).catch((error) => {
          console.error("Error performing hitTest for sample georeferences:", error);
        });
      });

      // Add map click listener to clear sample selection when clicking on empty areas
      mapState.view.on("click", (event) => {
        // Skip if in parcel selection mode
        if (argisFeature.activeAction === "parcel-georeference-selection" || isLpisLinking()) {
          return;
        }
        
        // Skip general map interaction during georeference editing mode
        if (isEditingGeoreferenceMarkers) {
          return; // No coordinate popups or selection clearing during georeference editing
        }

        // Check if click hit any graphics
        mapState.view.hitTest(event).then((response) => {
          const onLpis = response.results.some((result) => lpisLayers.includes(result.layer));
          const onLivefarm = response.results.some((result) => !lpisLayers.includes(result.layer));
          if (onLivefarm) {
            return;
          }

          clearSampleSelection();

          // A drawn LPIS parcel answers with its own panel, so no coordinates
          if (onLpis) {
            if (coordinatePopup.show) {
              closeCoordinatePopup();
            }
            return;
          }

          // Clicked on empty area
          if (coordinatePopup.show) {
            // If popup is already shown, dismiss it and clear marker
            closeCoordinatePopup();
          } else {
            // Show coordinate popup with marker
            showCoordinatePopup(event.mapPoint);
          }
        });
      });
      
      // listener for showing location marker on the map
      $emitter.on("showLocationMarker", (payload) => {
        let coordinates;
        
        // Handle both direct coordinates array and payload object
        if (Array.isArray(payload)) {
          coordinates = payload;
        } else if (payload && payload.coordinates) {
          coordinates = payload.coordinates;
        }
        
        if (coordinates && coordinates.length === 2) {
          // Clear existing markers from the farmerLayer first
          farmerLayer.removeAll();
          
          // Create a point geometry
          const point = new Point({
            longitude: coordinates[0],
            latitude: coordinates[1]
          });
          
          // Create marker symbol using the existing method
          const markerSymbol = argisFeature.createMarker("location");
          
          // Create a graphic with the point and symbol
          const locationGraphic = new Graphic({
            geometry: point,
            symbol: markerSymbol
          });
          
          // Add the graphic to the farmer layer
          farmerLayer.add(locationGraphic);
          
          // Zoom to the location
          mapState.view.goTo({
            center: coordinates,
            scale: scaleForZoom(15)
          });
        }
      });

      // Its own container, so placeMapControls can move it into the Expand
      searchWidget = new Search({
        view: mapState.view,
        container: document.createElement("div"),
      });
      searchExpand = new Expand({
        view: mapState.view,
        expandIcon: "search",
        expandTooltip: "Search",
        mode: "floating",
        // Closes once the map goes to a search result
        autoCollapse: true,
        // Opening it closes the layers panel, so the two never overlap
        group: "map-panels",
      });

      locateWidget = new Locate({
        view: mapState.view,
        graphic: new Graphic({
          symbol: { type: "simple-marker" },
        }),
      });

      //we move the adding of layer to map below because we want the LPIS and Dropoff location to be added first

      // Initialize soil analysis composable with location layers to preserve
      const { soilAnalysisLayer, soilAnalysisLabelLayer, updateSoilAnalysisDisplay, clearSoilAnalysisDisplay } = 
        useSoilAnalysisLayer(mapState, clearAllLayerDrawedOnMap, smapSamplePathsLayer, farmerLayer, farmerLocationsLayer, farmerLabelLayer, taskLocationsLayer, taskLabelLayer, farmFieldsLayer, parcelLabelLayer, samplePathsLabelLayer);
      
      // Add soil analysis layers to layerRefs so they can be controlled by the toggle
      layerRefs.soilAnalysisLayer = soilAnalysisLayer;
      layerRefs.soilAnalysisLabelLayer = soilAnalysisLabelLayer;

      mapState.map.addMany([
        farmFieldsLayer, // parcels layers
        farmSampleZoneLayer, // sample zone layers
        soilAnalysisLayer,
        soilAnalysisLabelLayer,
        sketchLayer,
        farmIdentifyLayer,
        parcelLabelLayer,
        sampleZoneLabelLayer,
        smapSamplePathsLayer, // sample paths (lines/points)
        samplePathsLabelLayer, // sample path labels (on top of paths)
        sampleGeoreferencesLayer, // sample georeference paths
        manualGeoreferencesLabelLayer, // manual georeference labels
        coordinateMarkerLayer, // coordinate click markers
        taskLocationsLayer, // task location pins
        taskLabelLayer, // task location labels
        farmerLocationsLayer, // farmer location pins
        farmerLabelLayer, // farmer location labels
        farmerLayer, //farm location pins
      ]);

      // Initialize label layer scales to match the global scale setting
      // This ensures all enabled label layers start with the correct minScale
      if (layerRefs.parcelLabelLayer && labelToggleStates.value.parcelLabels) {
        layerRefs.parcelLabelLayer.minScale = globalLabelScale;
      }
      if (layerRefs.samplePathsLabelLayer && labelToggleStates.value.samplePathLabels) {
        layerRefs.samplePathsLabelLayer.minScale = globalLabelScale;
      }
      if (layerRefs.sampleZoneLabelLayer && labelToggleStates.value.sampleZoneLabels) {
        layerRefs.sampleZoneLabelLayer.minScale = globalLabelScale;
      }
      if (taskLabelLayer) {
        taskLabelLayer.minScale = globalLabelScale;
      }
      if (farmerLabelLayer) {
        farmerLabelLayer.minScale = globalLabelScale;
      }

      mapState.view.when(() => {
        if (showFarmManager) {
          // mapState.view.ui.add(farmManager, "top-right");
        }
        mapState.view.ui.add(snappingToggleEl.value, "bottom-left");
        mapState.view.ui.add(soilWidgetsEl.value, "bottom-left");
        // The layer toggle opens from a button and starts closed
        if (showLayerToggle.value && layerToggleEl.value) {
          layersExpand = new Expand({
            view: mapState.view,
            content: layerToggleEl.value,
            expandIcon: "layers",
            expandTooltip: "Map layers",
            mode: "floating",
            group: "map-panels",
          });
        }
        if (layersExpand) mapState.view.ui.add(layersExpand, "top-left");
        // Added unplaced; placeMapControls adds the search and puts them in their corner
        mapState.view.ui.add(locateWidget);
        placeMapControls();
        narrowScreen.addEventListener("change", placeMapControls);

        // Zoom-based visibility is now handled by minScale/maxScale properties on GraphicsLayers
        
        // Add zoom watcher to log current scale for debugging
        reactiveUtils.on(
          () => mapState.view,
          "zoom",
          function () {
            const currentScale = mapState.view.scale;
            const currentZoom = mapState.view.zoom;
          }
        );
      });

      // Layer visibility is now handled by minScale/maxScale properties on GraphicsLayers
      // No need for manual zoom-based layer management

      watch(paths, (paths, oldPaths) => {
        // Prevent unnecessary re-execution
        if (paths === oldPaths) return;

        smapSamplePathsLayer.removeAll();
        samplePathsLabelLayer.removeAll();
        locationToZoom = [];

        // Handler for paths watcher, only runs when samples layer is on
        function handlePathsUpdate(paths, oldPaths) {
          const map = mapState.map;
          const sampleLabelsLayerActive = map && map.layers.includes(samplePathsLabelLayer);

          if (Array.isArray(paths) && paths.length > 0) {
            paths.forEach((path) => {
              if (!path.paths_xy || path.paths_xy.length < 1) return;

              //use createPathsLayer from LayerBuilder to create the path
              const { samplePath, sampleLabel, georeferenceMarkers } = createPathsGraphic(path);

              // Only add to map and zoom array if graphics were successfully created
              if (samplePath) {
                locationToZoom.push(samplePath);
                smapSamplePathsLayer.add(samplePath);
              }

              if (sampleLabel) {
                if (sampleLabelsLayerActive) {
                  samplePathsLabelLayer.add(sampleLabel);
                } else {
                }
              }

              // Add georeference markers if they exist
              if (georeferenceMarkers && georeferenceMarkers.length > 0) {
                georeferenceMarkers.forEach(marker => {
                  if (marker) {
                    smapSamplePathsLayer.add(marker);
                  }
                });
              }
            });
          }
        }

        // Register the watcher initially (samples layer is on by default) 
        // Check if paths is defined before watching
        if (paths && paths.value !== undefined) {
          stopPathsWatcher = watch(paths, handlePathsUpdate, { immediate: true });
        }

        // Use safer access to avoid reactive dependencies
        const rootData = internalInstance?.root?.data;

        //only zoom where the path have zoom as true and also the allowTOlocationOnMap is not set yet
        // with this there will not be an intrusion of mapState.view.goto
        if (
          locationToZoom.length > 0 &&
          rootData?.paths?.zoom &&
          (rootData?.paths?.forceZoom || // Allow task views to force zoom
            !rootData?.farmer?.hasOwnProperty("name") ||
            rootData?.farmer?.hasOwnProperty("allowToLocationOnMap"))
        ) {
          zoomToLocation(locationToZoom);

          // Clean up forceZoom flag after zoom to prevent affecting other interactions
          // Don't directly mutate reactive data
          if (rootData?.paths?.forceZoom) {
            setTimeout(() => {
              const pathsData = internalInstance?.root?.data?.paths;
              if (pathsData && pathsData.forceZoom) {
                pathsData.forceZoom = false;
              }
            }, 1500); // Clean up after zoom animation completes
          }
        }
      });
      // <-- removed extra closing bracket here

      watch(fields, (field, oldField) => {
        if (field === oldField) return;

        // Keep the sample paths, georeferences and their labels while a parcel is edited
        clearAllLayerDrawedOnMap([
          smapSamplePathsLayer,
          samplePathsLabelLayer,
          sampleGeoreferencesLayer,
          manualGeoreferencesLabelLayer,
        ]);

        locationToZoom = [];
        if (Array.isArray(field)) {
          field.forEach((path) => {
            // All items in the fields array should be parcel editing items with type: "edit"
            if (path?.type == "edit") {
              // Create a proper polygon graphic for parcel editing
              const editingGraphic = createParcelEditingGraphic(path);
              locationToZoom.push(editingGraphic);
              
              argisFeature.activeSketchTool(
                {
                  sketchLayer: sketchLayer,
                  smapSamplePathsLayer: smapSamplePathsLayer,
                  farmFieldsLayer: farmFieldsLayer,
                  farmSampleZoneLayer: farmSampleZoneLayer,
                  parcelLabelLayer: parcelLabelLayer,
                  sampleZoneLabelLayer: sampleZoneLabelLayer,
                  snapLayers: lpisSnapLayers(),
                  view: mapState.view,
                },
                $emitter,
                { action: "edit" },
                editingGraphic
              );
              snappingControl.show = true;
              return;
            }
            
            // This should never happen - log error if it does
            console.error('Unexpected non-edit item in fields array:', path);
          });
        }
        //zoom to section of the map
        let options = {
          duration: 1000,
        };
        mapState.view.goTo(locationToZoom, options).catch(function (error) {
          console.log(error, "3");
        });
      });

      watch(sampleZones, updateSoilAnalysisDisplay);
    };

    // True while LPIS parcels are being linked to a farmer; other clicks pause
    function isLpisLinking() {
      return !!internalInstance?.root?.data?.lpisLinking;
    }

    // True while another tool owns map clicks
    function isMapBusy() {
      return (
        isLpisLinking() ||
        isEditingGeoreferenceMarkers ||
        !!argisFeature?.activeAction && argisFeature.activeAction.includes("selection") ||
        argisFeature?.sketchViewModel?.state === "active"
      );
    }

    function setLPISLayer(layer, allLayers = []) {
      lpisLayers = allLayers;
    }

    function setFeaturesLocations(locations) {
      featuresLocations = locations;
    }

    // The farmer's linked DAFM LPIS parcels join the farmer's zoom extent.
    // When they load before the farmer zoom, switchMapView includes them;
    // when they load after it, the map zooms again, once per farmer opened.
    let lpisZoomedFarmer = null;
    function setLpisLinkedFeatures(graphics, farmerHashid) {
      if (!argisFeature || !farmerHashid) return;
      // Only the first load for the farmer just opened zooms; later loads,
      // such as after saving links, leave the map where it is
      const current = internalInstance?.root?.data?.farmer;
      const firstLoad = current?.hashid === farmerHashid && lpisZoomedFarmer !== farmerHashid && !isLpisLinking();
      if (firstLoad) lpisZoomedFarmer = farmerHashid;
      argisFeature.addToFarmerExtent(farmerHashid, graphics, { zoom: firstLoad && current?.zoom === true });
    }

    // Once the user has linked parcels for this farmer, the map stays where
    // they left it, even if the farmer had no links before
    watch(
      () => internalInstance?.root?.data?.lpisLinking,
      (linking) => {
        const hashid = internalInstance?.root?.data?.farmer?.hashid;
        if (linking && hashid) lpisZoomedFarmer = hashid;
      }
    );

    function zoomToLocation(
      location,
      options = {
        duration: 1000,
      }
    ) {
      // Check if view is ready and location is valid
      if (!mapState.view || !location) {
        return;
      }
      
      // Use the same simple options pattern as other working goTo calls in this file
      let zoomOptions = {
        duration: 1000,
      };
      
      mapState.view.goTo(location, zoomOptions).catch(function (error) {
        console.log(error, "zooming sample paths");
      });
    }
    onMounted(setupMap);

    onBeforeUnmount(() => {
      narrowScreen.removeEventListener("change", placeMapControls);
      // Clean up map resources and prevent memory leaks
      if (mapState.view) {
        mapState.view.destroy();
      }
      if (mapState.map) {
        mapState.map.destroy();
      }
    });

    return {
      setupMap,
      mapState,
      farmer,
      apikey,
      sampleZones,
      setLPISLayer,
      isMapBusy,
      setFeaturesLocations,
      setLpisLinkedFeatures,
      layerRefs,
      onLayerVisibilityChanged,
      onLayerOpacityChanged,
      onGlobalLabelScaleChanged,
      showLayerToggle,
      lpisContextVisible,
      lpisLinkedVisible,
      coordinatePopup,
      closeCoordinatePopup,
      copyCoordinates,
      onSoilDataUpdated,
      soilAnalysisLegendData,
      soilAnalysisEnabled,
      hasSoilAnalysisResults,
      snappingControl,
      snappingToggleEl,
      layerToggleEl,
      soilWidgetsEl,
      toggleSnapping,
    }; // farmManager
  },
};
</script>

<style>
@import url("https://js.arcgis.com/4.21/@arcgis/core/assets/esri/themes/light/main.css");

#viewDiv {
  position: relative;
}

/* ArcGIS lays out its bottom corners in a row. The bottom-right corner holds
   the map controls on wide screens (placeMapControls), so it stacks them in a
   column, right-aligned, instead. */
#viewDiv .esri-ui-bottom-right {
  flex-flow: column;
  align-items: flex-end;
}

#viewDiv .esri-ui-bottom-right .esri-component {
  margin-left: 0;
  margin-top: 10px;
}

/* The soil legend and property selector, stacked in the bottom-left corner */
#viewDiv .soil-widgets {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

/* Coordinate popup styles */
.coordinate-popup-widget {
  position: absolute;
  z-index: 99;
  pointer-events: auto;
  cursor: pointer;
}

.coordinate-popup-content {
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  backdrop-filter: blur(10px);
  transition: all 0.2s ease;
}

.coordinate-popup-content:hover {
  background: rgba(255, 255, 255, 1);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.coordinate-popup-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  font-weight: 600;
  color: #374151;
}

.coordinate-icon {
  width: 16px;
  height: 16px;
  color: #6366f1;
}

.coordinate-title {
  font-size: 14px;
  flex: 1;
}

.coordinate-close-btn {
  width: 16px;
  height: 16px;
  color: #9ca3af;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.coordinate-close-btn:hover {
  color: #6b7280;
}

.coordinate-close-btn svg {
  width: 12px;
  height: 12px;
}

.coordinate-values {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.15s ease;
}

.coordinate-values:hover {
  background-color: rgba(99, 102, 241, 0.1);
}

.coordinate-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
}

.coordinate-label {
  font-weight: 500;
  color: #6b7280;
  min-width: 30px;
}

.coordinate-value {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  color: #1f2937;
  font-weight: 500;
}

.coordinate-copy-hint {
  font-size: 11px;
  color: #9ca3af;
  text-align: center;
  border-top: 1px solid #f3f4f6;
  padding-top: 6px;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .coordinate-popup-content {
    min-width: 180px;
    padding: 10px;
  }
  
  .coordinate-popup-widget {
    bottom: 15px !important;
  }
}
</style>
